import { useState } from 'react'
import { motion } from 'framer-motion'
import { Search, Calendar, Clock, ChevronRight, Tag, TrendingUp, Newspaper, ArrowRight } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Link } from 'react-router-dom'
import { mockNews, NEWS_CATEGORIES } from '@/data/news-blogs-data'

const trendingNews = mockNews.slice(0, 4)

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function NewsPage() {
    const [search, setSearch] = useState('')
    const [activeCategory, setActiveCategory] = useState('All')

    const filtered = mockNews.filter(n => {
        const matchesSearch = n.title.toLowerCase().includes(search.toLowerCase()) || n.excerpt.toLowerCase().includes(search.toLowerCase())
        const matchesCat = activeCategory === 'All' || n.category === activeCategory
        return matchesSearch && matchesCat
    })

    const featured = filtered.find(n => n.featured)
    const rest = filtered.filter(n => !n.featured || activeCategory !== 'All' || search)

    return (
        <div className="min-h-screen relative">
            {/* Hero */}
            <section className="relative pt-32 pb-16 overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            <Newspaper className="h-4 w-4" /> Latest Updates
                        </span>
                        <h1 className="text-4xl md:text-6xl font-display font-bold mb-4">
                            <span className="text-theme-primary">NEET &amp; Medical</span>
                            <span className="block mt-1 gradient-text">News &amp; Updates</span>
                        </h1>
                        <p className="text-lg text-theme-secondary">
                            Stay informed with the latest NEET exam news, policy changes, results, and medical education updates — curated for aspiring doctors.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Search & Filters */}
            <section className="py-6 sticky top-20 z-30">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard className="p-4">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                            <div className="relative flex-1 w-full md:w-auto">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Search news articles..."
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                />
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1 w-full md:w-auto">
                                {NEWS_CATEGORIES.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveCategory(cat)}
                                        className={`shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeCategory === cat
                                            ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                            : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            </section>

            <section className="py-10">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Featured Article */}
                            {featured && !search && activeCategory === 'All' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Link to={`/news/${featured.id}`}>
                                        <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300">
                                            <div className="relative h-64 overflow-hidden">
                                                <img
                                                    src={featured.image}
                                                    alt={featured.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                                <div className="absolute top-4 left-4 flex gap-2">
                                                    <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                                        {featured.tag}
                                                    </span>
                                                    <span className="px-3 py-1 bg-primary-blue text-white text-xs font-medium rounded-full">
                                                        {featured.category}
                                                    </span>
                                                </div>
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <h2 className="text-white text-xl font-bold leading-tight">{featured.title}</h2>
                                                </div>
                                            </div>
                                            <div className="p-5">
                                                <p className="text-theme-secondary text-sm leading-relaxed mb-4">{featured.excerpt}</p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4 text-xs text-theme-muted">
                                                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(featured.date)}</span>
                                                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{featured.readTime}</span>
                                                    </div>
                                                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-blue group-hover:gap-2 transition-all">
                                                        Read Full Story <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                </div>
                                            </div>
                                        </GlassmorphicCard>
                                    </Link>
                                </motion.div>
                            )}

                            {/* News Grid */}
                            {(search || activeCategory !== 'All' ? filtered : rest).length === 0 ? (
                                <GlassmorphicCard className="p-12 text-center">
                                    <Newspaper className="h-12 w-12 text-theme-muted mx-auto mb-3" />
                                    <p className="text-theme-secondary">No news articles found for your search.</p>
                                </GlassmorphicCard>
                            ) : (
                                <div className="space-y-5">
                                    {(search || activeCategory !== 'All' ? filtered : rest).map((article, idx) => (
                                        <motion.div
                                            key={article.id}
                                            initial={{ opacity: 0, y: 15 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.07 }}
                                        >
                                            <Link to={`/news/${article.id}`}>
                                                <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-300">
                                                    <div className="flex flex-col sm:flex-row gap-0">
                                                        <div className="sm:w-48 h-40 sm:h-auto overflow-hidden shrink-0">
                                                            <img
                                                                src={article.image}
                                                                alt={article.title}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                            />
                                                        </div>
                                                        <div className="p-5 flex-1 flex flex-col justify-between">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="px-2.5 py-0.5 bg-primary-blue/10 text-primary-blue text-xs font-semibold rounded-full">
                                                                        {article.category}
                                                                    </span>
                                                                    <span className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-theme-muted text-xs rounded-full">
                                                                        {article.tag}
                                                                    </span>
                                                                </div>
                                                                <h3 className="font-bold text-theme-primary text-base leading-snug mb-2 group-hover:text-primary-blue transition-colors">
                                                                    {article.title}
                                                                </h3>
                                                                <p className="text-sm text-theme-secondary line-clamp-2">{article.excerpt}</p>
                                                            </div>
                                                            <div className="flex items-center justify-between mt-3">
                                                                <div className="flex items-center gap-3 text-xs text-theme-muted">
                                                                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(article.date)}</span>
                                                                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{article.readTime}</span>
                                                                </div>
                                                                <span className="flex items-center gap-1 text-xs font-semibold text-primary-blue">
                                                                    Read <ChevronRight className="h-3.5 w-3.5" />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </GlassmorphicCard>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Trending */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <TrendingUp className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Trending Now</h3>
                                </div>
                                <div className="space-y-4">
                                    {trendingNews.map((item, idx) => (
                                        <Link key={item.id} to={`/news/${item.id}`} className="flex gap-3 group cursor-pointer">
                                            <span className="text-2xl font-bold text-primary-blue/20 font-display leading-none w-8 shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                                            <div>
                                                <p className="text-sm font-medium text-theme-primary group-hover:text-primary-blue transition-colors line-clamp-2">{item.title}</p>
                                                <p className="text-xs text-theme-muted mt-1">{formatDate(item.date)}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* Categories */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Tag className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Categories</h3>
                                </div>
                                <div className="space-y-2">
                                    {NEWS_CATEGORIES.filter(c => c !== 'All').map(cat => {
                                        const count = mockNews.filter(n => n.category === cat).length
                                        return (
                                            <button
                                                key={cat}
                                                onClick={() => setActiveCategory(cat)}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${activeCategory === cat
                                                    ? 'bg-primary-blue text-white'
                                                    : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <span>{cat}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{count}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </GlassmorphicCard>

                            {/* CTA */}
                            <GlassmorphicCard className="p-5 bg-gradient-to-br from-primary-blue/10 to-blue-600/10 border-primary-blue/20">
                                <h3 className="font-bold text-theme-primary mb-2">Stay Updated</h3>
                                <p className="text-sm text-theme-secondary mb-4">Get the latest NEET news and updates directly — never miss an important announcement.</p>
                                <Link to="/contact">
                                    <button className="w-full py-2.5 bg-primary-blue hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-colors">
                                        Get Notified
                                    </button>
                                </Link>
                            </GlassmorphicCard>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
